import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { MoreHorizontal, Trash2, Edit3, MessageCircle, GripVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Tag color lookup
const tagLookup = {
  "urgent-important": { name: "Urgent and Important", color: "#e74c3c" },
  "urgent-not-important": { name: "Urgent Not Important", color: "#e67e22" },
  "not-urgent-important": { name: "Not Urgent & Important", color: "#3498db" },
  "not-urgent-not-important": { name: "Not Urgent and Not Important", color: "#27ae60" },
  "work-completed": { name: "Work Completed", color: "#9b59b6" },
  "in-process": { name: "In Process", color: "#1abc9c" },
};

export default function TaskCard({ task, lists, customTags, onToggle, onEdit, onDelete, isDragging }) {
  const { attributes, listeners, setNodeRef, transform, isDragging: isDragActive } = useDraggable({
    id: task.task_id,
  });

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
  } : undefined;

  const getTaskTags = () => {
    if (!task.tags || task.tags.length === 0) return [];
    return task.tags.map(tagId => {
      if (tagLookup[tagId]) return tagLookup[tagId];
      const custom = customTags?.find(t => t.tag_id === tagId);
      if (custom) return { name: custom.name, color: custom.color };
      return null;
    }).filter(Boolean);
  };

  const taskTags = getTaskTags();

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString().slice(-2);
    return `${day}.${month}.${year}`;
  };

  const getListInfo = (listId) => {
    return lists?.find(l => l.list_id === listId);
  };

  const list = getListInfo(task.list_id);
  const dateStr = formatDate(task.due_date);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`anydo-task-card p-3 group cursor-grab active:cursor-grabbing ${
        isDragging || isDragActive ? 'opacity-90 shadow-xl ring-2 ring-blue-500' : ''
      }`}
      data-testid={`task-${task.task_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing p-1 -ml-1 hover:bg-white/10 rounded transition-opacity"
        >
          <GripVertical className="w-4 h-4 text-gray-500" />
        </div>

        {/* Checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle?.(task.task_id); }}
          className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 transition-colors flex items-center justify-center ${
            task.completed 
              ? 'border-green-500 bg-green-500' 
              : 'border-gray-500 hover:border-gray-300'
          }`}
          data-testid={`task-checkbox-${task.task_id}`}
        >
          {task.completed && (
            <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>
        
        {/* Content */}
        <div className="flex-1 min-w-0" onClick={() => onEdit?.(task)}>
          {/* Tags row - above title */}
          {(taskTags.length > 0 || dateStr) && (
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              {taskTags.map((tag, i) => (
                <span
                  key={i}
                  className="text-[10px] px-1.5 py-0.5 rounded text-white font-medium"
                  style={{ backgroundColor: tag.color }}
                >
                  {tag.name}
                </span>
              ))}
              {dateStr && (
                <span className="text-[10px] text-gray-500">{dateStr}</span>
              )}
            </div>
          )}

          <p className={`text-sm leading-relaxed ${
            task.completed 
              ? 'text-gray-500 line-through' 
              : 'text-white/90'
          }`}>{task.title || "Untitled"}</p>

          {/* Added-by attribution (shown when an admin/EA added this task for the user) */}
          {task.added_by_email && task.added_by_name && (
            <div className="mt-1.5" data-testid={`task-added-by-${task.task_id}`}>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30"
                title={`Added by ${task.added_by_name} (${task.added_by_email})`}
              >
                Added by {task.added_by_name}
              </span>
            </div>
          )}
          
          {/* List indicator and comments */}
          {(list || task.description) && (
            <div className="flex items-center gap-2 mt-1.5">
              {task.description && (
                <MessageCircle className="w-3.5 h-3.5 text-gray-500" />
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {onDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity"
              >
                <MoreHorizontal className="w-4 h-4 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#2d3136] border-white/10">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit?.(task); }}>
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-400"
                onClick={(e) => { e.stopPropagation(); onDelete?.(task.task_id); }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
